import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api, useAuth } from "../App";
import {
  Brain,
  Cloud,
  GitBranch,
  Shield,
  Code,
  Smartphone,
  BarChart,
  Link,
  Settings,
  Layout,
  Palette,
  Rocket,
  Users,
  TrendingUp,
  Github,
  Terminal,
  ArrowRight,
  ArrowLeft,
  Check,
  List,
  Flame,
  BookOpen,
  Lightbulb,
  FlaskConical,
} from "lucide-react";

const SENIORITY_OPTIONS = [
  { value: "software-engineer", label: "Software Engineer" },
  { value: "senior-engineer", label: "Senior Engineer" },
  { value: "staff-engineer", label: "Staff / Principal" },
  { value: "tech-lead", label: "Tech Lead" },
  { value: "engineering-manager", label: "Eng Manager" },
  { value: "director", label: "Director" },
  { value: "vp", label: "VP of Eng" },
  { value: "cto", label: "CTO" },
];

const POST_STYLE_OPTIONS = [
  { value: "technical", label: "Technical Depth", description: "Code examples, architectural breakdowns, framework deep-dives", icon: Code },
  { value: "story", label: "Personal Story", description: "Relatable narratives, lessons learned from real experience", icon: BookOpen },
  { value: "leadership", label: "Leadership Angle", description: "Strategic insights, team dynamics, org-level decisions", icon: TrendingUp },
  { value: "listicle", label: "Listicle / Tips", description: "Numbered takeaways, quick wins, actionable checklists", icon: List },
  { value: "contrarian", label: "Contrarian Take", description: "Hot takes, challenging conventional wisdom, debate-sparking", icon: Flame },
  { value: "case-study", label: "Case Study", description: "Problem → approach → measurable results with real data", icon: FlaskConical },
  { value: "insight", label: "Industry Insight", description: "Trend analysis, market observations, forward-looking takes", icon: Lightbulb },
];

const TECH_KEYWORD_OPTIONS = [
  "Python", "JavaScript", "TypeScript", "Go", "Rust", "Java", "C++", "Swift", "Kotlin", "SQL",
  "React", "Vue", "Angular", "Next.js", "Tailwind CSS",
  "Node.js", "Django", "FastAPI", "Spring Boot", "Rails",
  "AWS", "Azure", "GCP", "Serverless",
  "LLMs", "PyTorch", "TensorFlow", "LangChain", "OpenAI API",
  "Docker", "Kubernetes", "Terraform", "CI/CD", "GitHub Actions",
  "PostgreSQL", "MongoDB", "Redis", "DynamoDB",
  "GraphQL", "REST APIs", "Microservices", "System Design",
];

const TOPIC_ICONS = {
  "ai-ml": Brain, "cloud": Cloud, "devops": GitBranch, "security": Shield,
  "web-dev": Code, "mobile": Smartphone, "data-science": BarChart,
  "blockchain": Link, "engineering": Settings, "product": Layout,
  "ux-design": Palette, "startup": Rocket, "leadership": Users,
  "career": TrendingUp, "open-source": Github, "programming": Terminal,
};

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1: Topics
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Step 2: Self-profile
  const [seniority, setSeniority] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [preferredStyles, setPreferredStyles] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await api.get("/topics");
        setTopics(response.data.topics);
      } catch (error) {
        toast.error("Failed to load topics. Please refresh the page.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTopics();
  }, []);

  const toggleTopic = (topicId) => {
    setSelectedTopics((prev) => {
      if (prev.includes(topicId)) return prev.filter((t) => t !== topicId);
      if (prev.length >= 7) { toast.error("You can select up to 7 topics"); return prev; }
      return [...prev, topicId];
    });
  };

  const toggleKeyword = (kw) => {
    setSelectedKeywords((prev) =>
      prev.includes(kw) ? prev.filter((k) => k !== kw) : [...prev, kw]
    );
  };

  const handleNextStep = () => {
    if (selectedTopics.length < 7) {
      toast.error("Please select at least 7 topics");
      return;
    }
    setStep(2);
  };

  const toggleStyle = (value) => {
    setPreferredStyles((prev) => {
      if (prev.includes(value)) return prev.filter((s) => s !== value);
      if (prev.length >= 3) { toast.error("You can select up to 3 styles"); return prev; }
      return [...prev, value];
    });
  };

  const handleFinish = async () => {
    setIsSaving(true);
    try {
      await api.post("/user/onboarding", {
        topics: selectedTopics,
        seniority: seniority || null,
        tech_keywords: selectedKeywords,
        preferred_post_style: preferredStyles,
      });
      setUser({ ...user, has_completed_onboarding: true });
      toast.success("Setup complete!");
      navigate("/feed", { replace: true });
    } catch (error) {
      toast.error("Failed to save settings. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = TOPIC_ICONS[iconName] || Code;
    return <IconComponent className="w-5 h-5" />;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ── Header with linear progress ── */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2.5">
              <img src="/logo.png" alt="PostWriter" className="w-7 h-7" />
              <span className="font-headline font-bold text-base tracking-tight text-primary">PostWriter</span>
            </div>

            {/* Step indicator — named steps, not just numbers */}
            <div className="flex items-center gap-3">
              {["Pick Topics", "Your Profile"].map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  {i > 0 && (
                    <div className={`w-8 h-px ${step > i ? "bg-primary" : "bg-border"}`} />
                  )}
                  <div className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors ${
                      step > i + 1
                        ? "bg-primary text-primary-foreground"
                        : step === i + 1
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium hidden sm:inline ${step === i + 1 ? "text-foreground" : "text-muted-foreground"}`}>
                      {label}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {user?.picture && (
              <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
            )}
          </div>

          {/* Thin progress bar */}
          <div className="h-0.5 bg-border -mx-4 sm:-mx-6">
            <div
              className="h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: step === 1 ? "12%" : "55%" }}
            />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10">

        {/* ── STEP 1: Topics ── */}
        {step === 1 && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-headline font-extrabold tracking-tight text-foreground mb-2">
                What do you post about?
              </h1>
              <p className="text-muted-foreground">
                Pick exactly 7 topics. We'll build your daily feed around them.
              </p>
            </div>

            {/* Dot counter — visual, not just a number */}
            <div className="flex items-center gap-3 mb-6">
              <div className="flex gap-1.5">
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 transition-all duration-200 ${
                      i < selectedTopics.length
                        ? "bg-primary border-primary"
                        : "bg-transparent border-border"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {selectedTopics.length}/7 selected
                {selectedTopics.length === 7 && (
                  <span className="text-primary font-semibold ml-1.5">— ready!</span>
                )}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
              {topics.map((topic) => {
                const isSelected = selectedTopics.includes(topic.id);
                const isDisabled = !isSelected && selectedTopics.length >= 7;
                return (
                  <button
                    key={topic.id}
                    onClick={() => !isDisabled && toggleTopic(topic.id)}
                    data-testid={`topic-${topic.id}`}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-150 ${
                      isDisabled ? "opacity-40 cursor-not-allowed" : "hover:-translate-y-0.5 hover:shadow-sm cursor-pointer"
                    } ${
                      isSelected
                        ? "border-primary bg-primary/8 dark:bg-primary/12"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-2.5 right-2.5 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-primary-foreground" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2.5 ${
                      isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}>
                      {getIcon(topic.id)}
                    </div>
                    <h3 className="font-headline font-bold text-sm text-foreground mb-0.5">{topic.name}</h3>
                    <p className="text-xs text-muted-foreground leading-snug">{topic.description}</p>
                  </button>
                );
              })}
            </div>

            <Button
              size="lg"
              onClick={handleNextStep}
              disabled={selectedTopics.length < 7}
              data-testid="continue-btn"
              className="w-full sm:w-auto px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-headline font-bold disabled:opacity-40"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {/* ── STEP 2: Professional Profile ── */}
        {step === 2 && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl sm:text-3xl font-headline font-extrabold tracking-tight text-foreground mb-2">
                Tell us about yourself
              </h1>
              <p className="text-muted-foreground">
                All optional — helps us write posts that sound like you.
              </p>
            </div>

            <div className="space-y-8">

              {/* Seniority — pill buttons, not a grid of cards */}
              <section>
                <h2 className="text-sm font-semibold text-foreground mb-3">Your Role</h2>
                <div className="flex flex-wrap gap-2">
                  {SENIORITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setSeniority(seniority === opt.value ? "" : opt.value)}
                      className={`px-4 py-2 rounded-full text-sm border-2 transition-all duration-150 ${
                        seniority === opt.value
                          ? "border-primary bg-primary/10 text-primary font-semibold"
                          : "border-border bg-card hover:border-primary/40 text-foreground font-medium"
                      }`}
                    >
                      {seniority === opt.value && <Check className="w-3 h-3 inline mr-1.5" />}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </section>

              {/* Post Style — compact cards, up to 3 */}
              <section>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-foreground">Preferred Post Style</h2>
                  <span className="text-xs text-muted-foreground">
                    <span className={preferredStyles.length === 3 ? "text-primary font-semibold" : ""}>
                      {preferredStyles.length}
                    </span>
                    /3 selected
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {POST_STYLE_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = preferredStyles.includes(opt.value);
                    const isDisabled = !isSelected && preferredStyles.length >= 3;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => toggleStyle(opt.value)}
                        disabled={isDisabled}
                        className={`relative p-3.5 rounded-xl border-2 text-left transition-all duration-150 ${
                          isDisabled ? "opacity-40 cursor-not-allowed" : "hover:-translate-y-0.5 hover:shadow-sm"
                        } ${
                          isSelected
                            ? "border-primary bg-primary/8 dark:bg-primary/12"
                            : "border-border bg-card hover:border-primary/40"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-4 h-4 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span className={`text-sm font-semibold ${isSelected ? "text-primary" : "text-foreground"}`}>
                            {opt.label}
                          </span>
                          {isSelected && (
                            <Check className="w-3.5 h-3.5 text-primary ml-auto shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{opt.description}</p>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Tech Keywords — pills */}
              <section>
                <h2 className="text-sm font-semibold text-foreground mb-1">Your Tech Stack</h2>
                <p className="text-xs text-muted-foreground mb-3">
                  Select the technologies you work with — they're woven into your posts.
                </p>
                <div className="flex flex-wrap gap-2">
                  {TECH_KEYWORD_OPTIONS.map((kw) => {
                    const isSelected = selectedKeywords.includes(kw);
                    return (
                      <button
                        key={kw}
                        onClick={() => toggleKeyword(kw)}
                        className={`px-3 py-1 rounded-full text-sm border transition-all duration-150 ${
                          isSelected
                            ? "border-primary bg-primary text-primary-foreground font-medium"
                            : "border-border bg-card text-foreground hover:border-primary/50"
                        }`}
                      >
                        {kw}
                      </button>
                    );
                  })}
                </div>
                {selectedKeywords.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">{selectedKeywords.length} selected</p>
                )}
              </section>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-10">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Button
                size="lg"
                onClick={handleFinish}
                disabled={isSaving}
                data-testid="continue-btn"
                className="px-10 bg-primary hover:bg-primary/90 text-primary-foreground font-headline font-bold disabled:opacity-40"
              >
                {isSaving ? "Saving…" : (
                  <>Finish Setup <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default OnboardingPage;
